import {
  GET_EVENT_REGISTER_STATE, GET_EVENT_REGISTER_DISPLAY,
  GET_EVENT_DETAILS_FOR_REGISTER_CONTAINER, GET_EVENT_DYNAMIC_DATA,
  GET_EVENT_ADDITIONAL_DATA_FOR_REGISTER_CONTAINER, GET_EVENT_NUM_OF_REMAINING_TICKETS,
} from '@/graphql/event.graphql'
import { createAttendeeSchema, serverURL } from '@/utils'
import { getEventPasswordState } from '@/utils/event-passwords'
import { waitingRoomHeaders, clearWaitingRoomSession } from '@/utils/waiting-room-session'
import axios from 'axios'
import i18next from 'i18next'

const displayFields = ['title', 'description', 'cover', 'coverImagePrimaryColor', 'location']
const monotonicNow = () => typeof performance !== 'undefined' ? performance.now() : Date.now()
// Organization 602 closes online sales 30 minutes before the event starts.
// Mirrored by EARLY_SALE_CLOSE in backend/server/services/register-display.ts.
const EARLY_SALE_CLOSE = { organizationId: 602, beforeEventMs: 30 * 60000 }
const unavailable = (event, now) => !!(event.soldOut || event.canceled || !event.applicationsOpen ||
  Date.parse(now) > Date.parse(event.to) ||
  (Number(event.organizationId) === EARLY_SALE_CLOSE.organizationId &&
    Date.parse(event.from) - Date.parse(now) <= EARLY_SALE_CLOSE.beforeEventMs))
const unsupportedSchema = error => {
  const errors = error && error.graphQLErrors

  return Array.isArray(errors) && errors.length > 0 && errors.every(item =>
    /Cannot query field "getEventRegister(State|Display)" on type "Query"/.test(item.message),
  )
}
const queryData = async (apollo, query, variables, context) => {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
  let timer
  try {
    const requestContext = { ...context, fetchOptions: { ...(context && context.fetchOptions), registerDeadline: true, ...(controller && { signal: controller.signal }) } }
    const result = await Promise.race([
      apollo.query({ query, variables, context: requestContext, fetchPolicy: 'no-cache', errorPolicy: 'none' }),
      new Promise((resolve, reject) => {
        timer = setTimeout(() => {
          if (controller) controller.abort()
          reject(new Error('Register request timed out'))
        }, 10000)
      }),
    ])
    if (!result || (result.errors && result.errors.length) || !result.data) {
      const error = new Error('Register data unavailable')
      error.graphQLErrors = result && result.errors
      throw error
    }

    return result.data
  } finally {
    clearTimeout(timer)
  }
}

export default {
  data: () => ({
    registerStateError: false,
    registerInitializationError: false,
    registerStatusRequest: 0,
    registerDisplayError: false,
    registerAdditionalError: false,
    registerRefreshing: false,
    registerServerNow: null,
    registerDisplayRevision: null,
    registerDisplayedRevision: null,
    hasActiveTicketSales: null,
    registerGeneration: 0,
    registerGateVersion: 0,
    registerInitialized: false,
    registerAdmissionPending: false,
    registerOrderGeneration: 0,
    registerOrderPending: false,
    registerHeldRestorePending: false,
    registerOrderHydration: null,
    registerOrderHydrationError: false,
    registerInventoryEpoch: 0,
    registerLegacyAPI: false,
    registerLastRefresh: 0,
    registerRetryAfter: 0,
    registerFailures: 0,
    registerBoundaryTimer: null,
    registerFollowupTimer: null,
    registerFocusTimer: null,
    registerGateOnly: false,
    registerGateUnsupported: false,
    registerHeldCheckout: false,
    registerPaymentRecovery: null,
    registerPendingPayment: null,
    registerRecoveryUnsupported: false,
    registerAdmissionRestoreNeeded: false,
  }),
  computed: {
    registerAccessLocked() {
      return this.scorpionsSaleDisplay === 'presale-unavailable' ||
        this.scorpionsSaleDisplay === 'general-unavailable' ||
        (!this.eventPasswordAccepted && (this.scorpionsSaleDisplay === 'presale-password' ||
          (!this.scorpionsSaleDisplay && this.event && this.event.flags && this.event.flags.eventPasswords &&
            this.eventPasswordState !== 'open')))
    },
    registerInventoryEnabled() {
      return !!(this.event && this.event.extraModules && this.event.extraModules.ticketing &&
        this.waitingRoomStatus === 'OK' && !this.isWaitingRoomCheckInProgress &&
        !this.registerAdmissionPending && !this.registerInitializationError && !this.registerStateError && !this.registerOrderPending && !this.registerHeldRestorePending && !this.registerOrderHydration &&
        !this.registerAccessLocked && !this.isEventUnavailable && this.hasActiveTicketSales !== false)
    },
    registerGateContext() {
      const now = Date.parse(this.registerServerNow)
      const active = ((this.event && this.event.eventPasswords) || []).filter(value =>
        Date.parse(value.startsAt) <= now && Date.parse(value.expiresAt) > now)

      return JSON.stringify([this.eventSlug, this.$route.query.sale, this.registerGateVersion,
        this.eventPasswordState, this.scorpionsSaleDisplay, active])
    },
  },
  watch: {
    eventSlug() { this.restartRegister() },
    activeStep(step, previous) {
      if (step === 1 && previous !== 1 && this.registerInitialized) this.refreshRegisterState('selection')
    },
    '$route.query.lang'() { this.restartRegister(false) },
  },
  mounted() {
    window.addEventListener('focus', this.handleRegisterFocus)
    document.addEventListener('visibilitychange', this.handleRegisterFocus)
    this.bootstrapRegister()
  },
  beforeDestroy() {
    this.registerGeneration++
    this.registerOrderGeneration++
    clearTimeout(this.registerBoundaryTimer)
    clearTimeout(this.registerFollowupTimer)
    clearTimeout(this.registerFocusTimer)
    window.removeEventListener('focus', this.handleRegisterFocus)
    document.removeEventListener('visibilitychange', this.handleRegisterFocus)
  },
  methods: {
    bootstrapRegister() {
      if (!this.eventSlug) return Promise.resolve()
      if (this._registerBootstrap) return this._registerBootstrap
      const generation = this.registerGeneration
      const task = (async () => {
        try {
          // A committed hold is a separate payment continuation, not a new
          // admission. Verify its event on the server before bypassing the queue.
          if (!this.registerInitialized && await this.prepareRegisterHeldOrder()) {
            if (generation !== this.registerGeneration) return
            this.registerHeldCheckout = true
            await this.refreshRegisterState('held-order')
            if (generation !== this.registerGeneration || this.registerStateError) return
            this.waitingRoomStatus = 'OK'
            await this.finishRegisterAdmission()
          } else {
            if (generation !== this.registerGeneration) return
            if (this.registerPaymentRecovery) {
              this.loadingDetails = false
              this.loadingDynamicData = false
              this.loadingAdditionalData = false
              this.isWaitingRoomCheckInProgress = false
              this.registerInitializationError = false

              return
            }
            await this.handleWaitingRoomCheck()
          }
        } catch (error) {
          if (generation !== this.registerGeneration) return
          this.registerInitializationError = true
          this.waitingRoomStatus = 'WAITING_ROOM'
          this.isWaitingRoomCheckInProgress = false
          this.loadingDetails = false
          this.loadingDynamicData = false
          this.loadingAdditionalData = false
        }
      })()
      this._registerBootstrap = task
      task.finally(() => { if (this._registerBootstrap === task) this._registerBootstrap = null })

      return task
    },
    restartRegister(newEvent = true) {
      // Locale is presentation context, not a new event. Keep the current
      // admission/hold restoration and additional-data request alive.
      if (!newEvent) {
        this.registerGateVersion++
        if (this.registerAdmissionPending) return this._registerBootstrap || this._registerAdmission
        if (!this.registerInitialized) return this.bootstrapRegister()

        return this.refreshRegisterState('locale')
      }
      this.registerGeneration++
      this.registerStatusRequest++
      this.registerInventoryEpoch++
      this.registerAdmissionPending = false
      this.registerGateVersion++
      this._registerRefresh = null
      this._registerBoundaryRefresh = null
      this._registerBootstrap = null
      this._registerAdmission = null
      this._registerDisplay = null
      this._registerNextDisplay = null
      clearTimeout(this.registerBoundaryTimer)
      clearTimeout(this.registerFollowupTimer)
      clearTimeout(this.registerFocusTimer)
      if (newEvent) {
        this.registerOrderGeneration++
        this.registerInitializationError = false
        this.registerOrderPending = false
        this.registerHeldRestorePending = false
        this._registerHeldRestore = null
        this.registerOrderHydration = null
        this.registerOrderHydrationError = false
        this.registerGateOnly = false
        this.registerHeldCheckout = false
        this.registerAdmissionRestoreNeeded = false
        this.registerPaymentRecovery = null
        this.registerPendingPayment = null
        this._registerPreparedOrder = null
        clearInterval(this.pollingInterval)
        clearInterval(this.checkoutHeartbeatInterval)
        this.event = null
        this.order = null
        this.orderItemAccessCode = null
        this.membership = null
        this.promoCode = null
        this.initialPromoCode = null
        this.subscription = []
        this.giftCard = null
        this.initialSeatSelect = null
        this.initialAttendee = {}
        this.ticketLocations = {}
        this.updatedOrderItems = {}
        this.removeSeat = []
        this.startTime = null
        this.ticketsInsurance = false
        this.ticketInsuranceAmount = null
        this.autoPromoCodeInProgress = false
        this.showMessage = false
        this.showError = false
        this.snackbarOpen = false
        this.showUserMessageDialog = false
        this.showBecomeMemberAlert = false
        this.organizationPageViewSent = false
        this.numberOfRemainingTickets = 0
        this.hasNoRemainingTickets = false
        this.hasActiveTicketSales = null
        this.customStyles = {}
        this.schema = {}
        this.loadingDetails = true
        this.loadingDynamicData = true
        this.loadingAdditionalData = true
        this.registerStateError = false
        this.registerDisplayError = false
        this.registerAdditionalError = false
        this.registerServerNow = null
        this.registerDisplayRevision = null
        this.registerRetryAfter = 0
        this.registerFailures = 0
        this.form = { attendee: {}, workshops: [] }
        this.shoppingCart = []
        this.activeStep = 1
        this.eventPasswordAccepted = false
        this.registerInitialized = false
        this.registerDisplayedRevision = null
        this.waitingRoomStatus = null
        this.isWaitingRoomCheckInProgress = true
        this.bootstrapRegister()
      } else if (!this.registerInitialized) {
        this.bootstrapRegister()
      } else {
        this.refreshRegisterState('locale')
      }
    },
    finishRegisterAdmission(delayed = false) {
      if (this._registerAdmission) return this._registerAdmission
      const generation = this.registerGeneration
      const task = this.completeRegisterAdmission(delayed)
      this._registerAdmission = task
      const clear = () => {
        if (this._registerAdmission === task) this._registerAdmission = null
        if (generation === this.registerGeneration) this.registerAdmissionPending = false
      }
      task.then(clear, clear)

      return task
    },
    async completeRegisterAdmission(delayed = false) {
      const generation = this.registerGeneration
      this.registerAdmissionPending = true
      if (!this.registerHeldCheckout && !this.registerInitialized) {
        const locked = await this.readRegisterGate(generation)
        if (generation !== this.registerGeneration) return
        if (locked) return
      }
      // A queued buyer may have waited through arbitrary organizer edits. Keep
      // inventory gated until the new snapshot and saved hold restoration settle.
      if (delayed || !this.registerServerNow) {
        if (this._registerRefresh) await this._registerRefresh
        if (generation !== this.registerGeneration) return
        await this.refreshRegisterState('admission')
        if (generation !== this.registerGeneration) return
        if (this.registerStateError) throw new Error('Admission state unavailable')
      }
      this.isWaitingRoomCheckInProgress = false
      this.loadingDynamicData = false
      // This initialization belongs only to queue admission, never live refresh.
      if (!this.registerInitialized) {
        try {
          await this.processSavedOrderAndOtherInitializations()
        } catch (error) {
          if (generation === this.registerGeneration) this.registerInitializationError = true
          throw error
        }
        if (generation !== this.registerGeneration) return
        this.registerInitializationError = false
        this.registerInitialized = true
      }
      await this.loadRegisterAdditional(generation)
      if (generation !== this.registerGeneration) return
      this.registerAdmissionPending = false
      try {
        await this.refreshRegisterInventory(generation)
      } catch (error) {
        if (generation === this.registerGeneration) this.registerStateError = true
      }
    },
    async readRegisterGate(generation = this.registerGeneration) {
      if (this.registerGateUnsupported) return false
      const started = monotonicNow()
      let data
      try {
        const response = await axios.get(`${serverURL}/register/gate?slug=${encodeURIComponent(this.eventSlug)}`,
          { withCredentials: true, headers: waitingRoomHeaders(), timeout: 15000 })
        data = response.data
      } catch (error) {
        const code = error.response && error.response.data && error.response.data.code
        if (code === 'WAITING_ROOM_REQUIRED' || code === 'WAITING_ROOM_CREDENTIAL_INVALID') {
          if (code === 'WAITING_ROOM_CREDENTIAL_INVALID') {
            clearWaitingRoomSession()
            this.guestId = null
          }
          this.waitingRoomStatus = 'WAITING_ROOM'
          this.loadingDetails = false
          this.loadingDynamicData = false
          this.loadingAdditionalData = false
          clearInterval(this.checkoutHeartbeatInterval)
          this.checkoutHeartbeatInterval = null
          this.startPolling(false)
          // Let this admission settle before starting a new one; never await
          // itself through handleWaitingRoomCheck after a lease has expired.
          clearTimeout(this.registerFollowupTimer)
          this.registerFollowupTimer = setTimeout(() => {
            if (generation === this.registerGeneration) this.pollStatus()
          }, 0)

          return true
        }
        // Explicit mixed-version fallback only; outages never trigger heavy reads.
        if (error.response && error.response.status === 404 &&
          (!error.response.data || error.response.data.code !== 'EVENT_NOT_FOUND')) {
          this.registerGateUnsupported = true

          return false
        }
        throw error
      }
      if (generation !== this.registerGeneration) return true
      const event = data && data.event
      if (!event || event.slug !== this.eventSlug || !event.extraModules ||
        typeof event.extraModules.ticketing !== 'boolean' || typeof data.hasActiveTicketSales !== 'boolean' ||
        !Number.isFinite(Date.parse(data.serverNow)) ||
        ['soldOut', 'canceled', 'applicationsOpen'].some(key => typeof event[key] !== 'boolean')) throw new Error('Incomplete sale gate')
      this.event = { ...this.event, ...event,
        organization: { ...(this.event && this.event.organization), id: event.organizationId } }
      this.registerServerNow = data.serverNow
      this.saleDisplayCheckedAt = Date.parse(data.serverNow)
      this.eventPasswordState = getEventPasswordState(event.eventPasswords || [], data.serverNow)
      this.hasActiveTicketSales = data.hasActiveTicketSales
      this.isEventUnavailable = unavailable(event, data.serverNow)
      this.registerGateOnly = !!(event.extraModules.ticketing && !this.order &&
        (this.isEventUnavailable || !data.hasActiveTicketSales || this.registerAccessLocked))
      if (!this.registerGateOnly) {
        // This snapshot is deliberately incomplete: still require full live state.
        this.registerServerNow = null

        return false
      }
      this.loadingDetails = false
      this.loadingDynamicData = false
      this.loadingAdditionalData = false
      this.isWaitingRoomCheckInProgress = false
      this.registerStateError = false
      this.registerInitializationError = false
      this._registerSnapshotStartedAt = started
      this.scheduleRegisterBoundary(data.nextSaleTransitionAt)

      return true
    },
    async loadRegisterAdditional(generation = this.registerGeneration) {
      try {
        const data = await queryData(this.$apollo, GET_EVENT_ADDITIONAL_DATA_FOR_REGISTER_CONTAINER, { slug: this.eventSlug })
        if (generation !== this.registerGeneration) return
        const additional = data.getEventAdditionalDataForRegisterContainer
        if (!additional) throw new Error('Missing additional data')
        const freshOrganization = {}
        for (const key of ['id', 'type', 'fixedFeePerTicket', 'variableFeePercentage']) {
          if (this.event.organization && key in this.event.organization) freshOrganization[key] = this.event.organization[key]
        }
        this.event = { ...this.event, organization: { ...additional.organization, ...freshOrganization } }
        this.customStyles = (additional.eventUtils && additional.eventUtils.inviteFormStyles) || {}
        this.sendOrganizationPageView()
        if (additional.translation) {
          for (const language in additional.translation.translationsJSON) {
            i18next.addResourceBundle(language, 'default', additional.translation.translationsJSON[language], true, false)
          }
        }
        this.registerAdditionalError = false
      } catch (error) {
        if (generation === this.registerGeneration) this.registerAdditionalError = true
      } finally {
        if (generation === this.registerGeneration) this.loadingAdditionalData = false
      }
    },
    async readRegisterState(slug) {
      if (!this.registerLegacyAPI) {
        try {
          const data = await queryData(this.$apollo, GET_EVENT_REGISTER_STATE, { slug })
          const state = data.getEventRegisterState
          if (!state || !state.event || !state.event.extraModules || !state.event.organization ||
            !Number.isFinite(Date.parse(state.serverNow)) || !state.displayRevision ||
            typeof state.hasActiveTicketSales !== 'boolean' || state.event.slug !== slug ||
            !state.event.id || typeof state.event.extraModules.ticketing !== 'boolean' ||
            ['soldOut', 'canceled', 'applicationsOpen'].some(key => typeof state.event[key] !== 'boolean')) throw new Error('Incomplete live state')

          return state
        } catch (error) {
          if (!unsupportedSchema(error)) throw error
          this.registerLegacyAPI = true
        }
      }
      // Rolling back the API uses fresh legacy reads, never a broad persisted blob.
      const details = await queryData(this.$apollo, GET_EVENT_DETAILS_FOR_REGISTER_CONTAINER, { slug })
      const dynamic = await queryData(this.$apollo, GET_EVENT_DYNAMIC_DATA, { slug })
      if (!details.getEventDetailsForRegisterContainer || !dynamic.getEventBasicData) throw new Error('Incomplete legacy state')

      return { event: { ...details.getEventDetailsForRegisterContainer, ...dynamic.getEventBasicData },
        serverNow: new Date().toISOString(), displayRevision: null, hasActiveTicketSales: null, nextSaleTransitionAt: null }
    },
    refreshRegisterState(reason = 'manual', allowRevisionRetry = true) {
      if (this.waitingRoomStatus === 'WAITING_ROOM' && !this.registerHeldCheckout) return Promise.resolve()
      if (this.registerGateOnly && !this.registerHeldCheckout) return this.finishRegisterAdmission()
      if (this._registerRefresh) {
        // A slow pre-boundary request cannot consume the only transition signal.
        // Coalesce concurrent signals into one later authoritative read.
        if ((reason === 'boundary' || reason === 'route') && !this._registerBoundaryRefresh) {
          const generation = this.registerGeneration
          const pending = this._registerRefresh
          this._registerBoundaryRefresh = pending
          pending.then(() => {
            if (this._registerBoundaryRefresh !== pending) return
            this._registerBoundaryRefresh = null
            if (generation !== this.registerGeneration || document.hidden) return
            clearTimeout(this.registerFollowupTimer)
            this.registerFollowupTimer = setTimeout(() => {
              if (generation !== this.registerGeneration || document.hidden) return
              if (Date.now() < this.registerRetryAfter) this.handleRegisterFocus()
              else this.refreshRegisterState(reason)
            }, Math.max(500, this.registerRetryAfter - Date.now()))
          })
        }

        return this._registerRefresh
      }
      const generation = this.registerGeneration
      const slug = this.eventSlug
      this.registerRefreshing = true
      const task = (async () => {
        try {
          const started = monotonicNow()
          const state = await this.readRegisterState(slug)
          if (generation !== this.registerGeneration) return
          this.event = { ...this.event, ...state.event,
            organization: { ...(this.event && this.event.organization), ...state.event.organization } }
          this.registerServerNow = state.serverNow
          this.saleDisplayCheckedAt = Date.parse(state.serverNow)
          this.registerDisplayRevision = state.displayRevision
          this.hasActiveTicketSales = state.hasActiveTicketSales
          // Closing fresh sales cannot remove the payment route for an existing
          // floor hold, including a buyer who already entered editing.
          if (this.order && this.event.venueId && this.activeStep === 1 && this.hasActiveTicketSales === false) {
            await this.restoreClosedRegisterHold()
            if (generation !== this.registerGeneration) return
          }
          this.eventPasswordState = getEventPasswordState(this.event.eventPasswords || [], state.serverNow)
          this.isEventUnavailable = unavailable(this.event, state.serverNow)
          this.customAttendeeFields = this.event.flags && this.event.flags.customAttendeeFields
          this.schema = createAttendeeSchema(this.event.attendeeFields)
          this.registerStateError = false
          this.registerFailures = 0
          this.registerRetryAfter = 0
          this.loadingDetails = false
          this.loadingDynamicData = false
          this.registerLastRefresh = Date.now()
          this._registerSnapshotStartedAt = started
          this.scheduleRegisterBoundary(state.nextSaleTransitionAt)
          if (state.displayRevision) this.refreshRegisterDisplay(state, generation, allowRevisionRetry)
          await this.refreshRegisterInventory(generation)
          if (reason === 'boundary' && generation === this.registerGeneration) {
            clearTimeout(this.registerFollowupTimer)
            this.registerFollowupTimer = setTimeout(() => {
              if (!document.hidden) this.refreshRegisterState('post-boundary')
            }, 500 + Math.floor(Math.random() * 250))
          }
        } catch (error) {
          if (generation !== this.registerGeneration) return
          this.registerStateError = true
          this.registerFailures++
          this.registerRetryAfter = Date.now() + Math.min(30000, 1000 * Math.pow(2, this.registerFailures))
          this.loadingDetails = false
          this.loadingDynamicData = false
        }
      })()
      this._registerRefresh = task
      task.finally(() => {
        if (this._registerRefresh === task) {
          this._registerRefresh = null
          this.registerRefreshing = false
        }
      })

      return task
    },
    async refreshRegisterDisplay(state, generation, allowRevisionRetry) {
      if (this.registerDisplayedRevision === state.displayRevision) return
      if (this._registerDisplay) {
        this._registerNextDisplay = { state, generation, allowRevisionRetry }

        return
      }
      const task = queryData(this.$apollo, GET_EVENT_REGISTER_DISPLAY,
        { slug: this.eventSlug, expectedRevision: state.displayRevision }, { registerServerNow: state.serverNow })
      this._registerDisplay = task
      try {
        const data = await task
        if (generation !== this.registerGeneration || state.displayRevision !== this.registerDisplayRevision) return
        const display = data.getEventRegisterDisplay
        if (!display || display.revision !== state.displayRevision) {
          if (allowRevisionRetry) {
            const pending = this._registerRefresh || Promise.resolve()
            pending.then(() => {
              if (generation === this.registerGeneration) this.refreshRegisterState('display-mismatch', false)
            })
          }
          throw new Error('Display revision changed')
        }
        if (display.slug !== this.eventSlug || Date.parse(display.expiresAt) <= Date.parse(state.serverNow) ||
          !Number.isFinite(Date.parse(display.expiresAt))) throw new Error('Expired display')
        const presentation = {}
        for (const key of displayFields) {
          if (!Object.prototype.hasOwnProperty.call(display, key)) throw new Error('Incomplete display')
          presentation[key] = display[key]
        }
        this.event = { ...this.event, ...presentation }
        this.registerDisplayedRevision = display.revision
        this.registerDisplayError = false
      } catch (error) {
        if (generation === this.registerGeneration) this.registerDisplayError = true
      } finally {
        if (this._registerDisplay === task) {
          this._registerDisplay = null
          const next = this._registerNextDisplay
          this._registerNextDisplay = null
          if (next && next.generation === this.registerGeneration) {
            this.refreshRegisterDisplay(next.state, next.generation, next.allowRevisionRetry)
          }
        }
      }
    },
    registerOrderContext() {
      return { generation: this.registerOrderGeneration, slug: this.eventSlug }
    },
    isCurrentRegisterOrder(context) {
      return context.generation === this.registerOrderGeneration && context.slug === this.eventSlug && !this._isDestroyed
    },
    async withRegisterOrderCreation(create) {
      if (this.registerPendingPayment) return
      if (this.registerOrderPending || this.registerHeldRestorePending) return
      // Order attempts outlive same-event display, locale and access refreshes.
      // Only another attempt, event replacement or destruction invalidates them.
      this.registerOrderGeneration++
      const context = this.registerOrderContext()
      this.registerOrderPending = true
      this.registerInventoryEpoch++
      // Invalidate child requests synchronously, before reactive props propagate.
      for (const child of [this.$refs.childList, this.$refs.childFloor]) {
        if (child) child._registerInventoryRequest = (child._registerInventoryRequest || 0) + 1
      }
      try {
        return await create()
      } catch (error) {
        if (!this.isCurrentRegisterOrder(context) || !await this.recoverRegisterAdmission(error)) throw error
      } finally {
        if (this.isCurrentRegisterOrder(context)) {
          // Let any pre-creation state read settle while selection is protected.
          if (!this.order && !this.registerOrderHydration && this._registerRefresh) await this._registerRefresh
          if (this.isCurrentRegisterOrder(context)) {
            this.registerOrderPending = false
            if (!this.order && !this.registerOrderHydration && this.waitingRoomStatus === 'OK') await this.refreshRegisterState('order-failed')
            else if (this.order && !this.registerOrderHydration && this.activeStep === 1 &&
              (this.hasActiveTicketSales === false || this.registerAdmissionRestoreNeeded)) {
              try {
                await this.restoreClosedRegisterHold()
              } catch (error) {
                if (this.isCurrentRegisterOrder(context)) this.registerStateError = true
              }
            }
          }
        }
      }
    },
    async refreshRegisterInventory(generation = this.registerGeneration) {
      if (this.registerAdmissionPending || this.registerOrderPending || this.registerHeldRestorePending || this.registerOrderHydration) return
      const epoch = this.registerInventoryEpoch
      // Editing an existing floor hold needs venue metadata, not a new availability count.
      if (this.order) {
        if (this.activeStep !== 1) return
        await this.$nextTick()
        if (generation !== this.registerGeneration) return
        const floor = this.$refs.childFloor
        if (floor && floor.refreshRegisterInventory) await floor.refreshRegisterInventory()

        return
      }
      if (!this.registerInventoryEnabled) return
      const accessCode = this.orderItemAccessCode
      const data = await queryData(this.$apollo, GET_EVENT_NUM_OF_REMAINING_TICKETS, { eventId: this.event.id })
      if (generation !== this.registerGeneration || epoch !== this.registerInventoryEpoch ||
        this.order || accessCode !== this.orderItemAccessCode || !this.registerInventoryEnabled) return
      const count = data.getEventNumOfRemainingTickets
      if (typeof count !== 'number' || !Number.isFinite(count)) throw new Error('Missing live inventory')
      this.numberOfRemainingTickets = count
      this.hasNoRemainingTickets = count <= 0
      await this.$nextTick()
      if (generation !== this.registerGeneration || epoch !== this.registerInventoryEpoch || this.order || !this.registerInventoryEnabled) return
      const selector = this.$refs.childList || this.$refs.childFloor
      if (selector && selector.refreshRegisterInventory) await selector.refreshRegisterInventory()
    },
    scheduleRegisterBoundary(boundary) {
      clearTimeout(this.registerBoundaryTimer)
      if (!boundary || document.hidden) return
      const elapsed = this._registerSnapshotStartedAt == null ? 0 : Math.max(0, monotonicNow() - this._registerSnapshotStartedAt)
      const delay = Date.parse(boundary) - Date.parse(this.registerServerNow) - elapsed
      if (!Number.isFinite(delay)) return
      this.registerBoundaryTimer = setTimeout(() => {
        if (!document.hidden) this.refreshRegisterState('boundary')
      }, Math.min(2147483000, Math.max(0, delay) + Math.floor(Math.random() * 150)))
    },
    handleRegisterFocus() {
      clearTimeout(this.registerFocusTimer)
      if (document.hidden) {
        clearTimeout(this.registerBoundaryTimer)
        clearTimeout(this.registerFollowupTimer)

        return
      }
      const generation = this.registerGeneration
      const delay = Math.max(150, this.registerRetryAfter - Date.now(), this.registerLastRefresh + 750 - Date.now())
      this.registerFocusTimer = setTimeout(() => {
        if (document.hidden || generation !== this.registerGeneration) return
        // A newer read may have moved the deadline while this callback was pending.
        if (Date.now() < Math.max(this.registerRetryAfter, this.registerLastRefresh + 750)) {
          this.handleRegisterFocus()

          return
        }
        if (!this.registerInitialized) this.bootstrapRegister()
        else this.refreshRegisterState('focus')
      }, delay)
    },
    async retryRegister() {
      if (this.registerPaymentRecovery) {
        await this.refreshRecoveredPayment()

        return
      }
      if (this.registerAdmissionRestoreNeeded) {
        try {
          await this.restoreClosedRegisterHold()
          this.registerStateError = false
        } catch (error) { this.registerStateError = true }

        return
      }
      if (this.registerOrderHydration) {
        await this.withRegisterOrderCreation(() => this.hydrateRegisterOrder(this.registerOrderHydration))

        return
      }
      if (!this.registerInitialized) await this.bootstrapRegister()
      else {
        await this.refreshRegisterState('retry')
        if (this.registerAdditionalError) await this.loadRegisterAdditional()
      }
    },
    async acceptRegisterPassword(context) {
      if (context !== this.registerGateContext) return
      this.eventPasswordAccepted = true
      this.registerGateVersion++
      await this.refreshRegisterState('unlock')
    },
  },
}
